import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { money, searchOpts, matchKw } from "@/lib/fmt";
import { PageHeader, Field, Empty } from "@/components/Common";
import { Autocomplete } from "@/components/Autocomplete";
import { ImportDialog } from "@/components/ImportDialog";
import { PhotoField, BrochureField, StoredImg } from "@/components/Uploads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function MasterPage({ entity, title, subtitle, fields, columns, searchKeys }) {
  const [rows, setRows] = useState([]);
  const [kw, setKw] = useState("");
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get(`/${entity}`).then((r) => setRows(r.data)), [entity]);
  useEffect(() => { load(); }, [load]);
  const list = useMemo(() => rows.filter((r) => matchKw(r, kw, searchKeys)), [rows, kw, searchKeys]);
  const blank = Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]));

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Nama wajib diisi");
    const body = Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? Number(form[f.key] || 0) : form[f.key] ?? ""]));
    try {
      if (form.id) await api.put(`/${entity}/${form.id}`, body); else await api.post(`/${entity}`, body);
      toast.success("Tersimpan");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    try { await api.delete(`/${entity}/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <ImportDialog entity={entity} title={title} onDone={load} />
        <Button onClick={() => setForm(blank)} data-testid={`new-${entity}-btn`}><Plus className="h-4 w-4 mr-1" />Tambah</Button>
      </PageHeader>
      <div className="card-premium fade-up">
        <div className="p-5"><Autocomplete value={kw} onChange={setKw} options={searchOpts(rows, searchKeys)} icon placeholder="Cari dengan keyword..." testId={`${entity}-search`} className="md:w-96" /></div>
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid={`${entity}-table`}>
            <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}<th /></tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.id} data-testid={`${entity}-row-${i}`}>
                  {columns.map((c) => <td key={c.key} className="text-sm">{c.render ? c.render(r) : r[c.key]}</td>)}
                  <td className="whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => setForm({ ...r })} data-testid={`${entity}-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`${entity}-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!list.length && <Empty />}
        </div>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-xl">{form?.id ? "Edit" : "Tambah"} {title}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid gap-4 md:grid-cols-2">
              {fields.filter((f) => !f.hidden).map((f) => (
                <Field key={f.key} label={f.label} className={f.wide ? "md:col-span-2" : ""}>
                  {f.render ? f.render(form, (p) => setForm({ ...form, ...p })) : f.options ? (
                    <select className="h-10 rounded-md border border-input bg-white px-3 text-sm" value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${entity}-form-${f.key}`}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
                  ) : f.suggest ? (
                    <Autocomplete value={form[f.key]} options={searchOpts(rows, [f.key])} onChange={(v) => setForm({ ...form, [f.key]: v })} testId={`${entity}-form-${f.key}`} />
                  ) : (
                    <Input type={f.type || "text"} className="bg-white" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${entity}-form-${f.key}`} />
                  )}
                </Field>
              ))}
              <Button className="md:col-span-2 h-11" onClick={save} data-testid={`${entity}-save-btn`}>Simpan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CUST_FIELDS = [
  { key: "name", label: "Nama PT", wide: true }, { key: "address", label: "Alamat", wide: true }, { key: "location", label: "Lokasi / Kota", suggest: true },
  { key: "industry", label: "Industri", suggest: true }, { key: "pic_name", label: "Nama PIC" }, { key: "pic_phone", label: "No. HP PIC" },
  { key: "email", label: "Email PIC (untuk kirim penawaran)" }, { key: "notes", label: "Catatan" },
];
const CUST_COLS = [
  { key: "name", label: "Nama PT", render: (r) => <span className="font-medium">{r.name}</span> }, { key: "location", label: "Lokasi" }, { key: "industry", label: "Industri" },
  { key: "pic_name", label: "PIC" }, { key: "pic_phone", label: "No. HP" }, { key: "sales_name", label: "Dibuat oleh" },
];
const PROD_FIELDS = [
  { key: "name", label: "Nama Barang / Jasa", wide: true }, { key: "category", label: "Kategori", suggest: true }, { key: "unit", label: "Satuan", default: "pcs" },
  { key: "unit_cost", label: "Harga Cost", type: "number", default: 0 }, { key: "currency", label: "Mata Uang", options: ["IDR", "USD"], default: "IDR" },
  { key: "description", label: "Deskripsi", wide: true },
  { key: "photo", label: "Foto Produk (opsional)", render: (f, set) => <PhotoField value={f.photo || ""} onChange={(v) => set({ photo: v })} testId="products-form-photo" /> },
  { key: "brochure_path", label: "Brosur / Data Sheet PDF (opsional)", render: (f, set) => <BrochureField path={f.brochure_path} name={f.brochure_name} onChange={set} testId="products-form-brochure" /> },
  { key: "brochure_name", hidden: true },
  { key: "internal_note", label: "Catatan Internal / Link Pembelian Terakhir", wide: true },
  { key: "note_history", label: "Riwayat Link / Catatan", wide: true, render: (f) => (
    <div className="max-h-40 space-y-1 overflow-auto rounded-md border bg-slate-50 p-2 text-xs" data-testid="products-note-history">
      {!(f.note_history || []).length && <span className="text-slate-400">Belum ada riwayat.</span>}
      {[...(f.note_history || [])].reverse().map((h, k) => (
        <div key={k} className="border-b pb-1 last:border-0"><span className="text-slate-400">{(h.at || "").slice(0, 10)} • {h.by}{h.ref ? ` • ${h.ref}` : ""}</span><p className="break-all">{h.note}</p></div>
      ))}
    </div>
  ) },
];
const PROD_COLS = [
  { key: "name", label: "Nama", render: (r) => <span className="flex items-center gap-2 font-medium">{r.photo && <StoredImg path={r.photo} className="h-8 w-8 rounded object-cover" />}{r.name}</span> }, { key: "category", label: "Kategori" }, { key: "unit", label: "Satuan" },
  { key: "unit_cost", label: "Harga Cost", render: (r) => <span className="font-num">{money(r.unit_cost, r.currency)}</span> }, { key: "description", label: "Deskripsi" },
];

export const Customers = () => <MasterPage entity="customers" title="Customer" subtitle="Data master customer / PT." fields={CUST_FIELDS} columns={CUST_COLS} searchKeys={["name", "location", "pic_name", "industry"]} />;
export const Products = () => <MasterPage entity="products" title="Barang / Jasa" subtitle="Data master barang & jasa beserta harga cost." fields={PROD_FIELDS} columns={PROD_COLS} searchKeys={["name", "category", "description"]} />;
